/**
 * Localisation map keyed by BCP-47 locale code.
 *
 * @example
 * {
 *   "en-US": "Aerial image",
 *   "cs-CZ": "Letecký snímek",
 *   "de-DE": "Luftbild"
 * }
 */
export type LocalisationMap = Record<string, string>;

type Updater = () => void;

/**
 * Lightweight i18n service.
 *
 * Resolves a value for the currently active locale from a {@link LocalisationMap},
 * with a deterministic fallback to a plain default string when:
 *  - no locale is set,
 *  - the map does not contain the active locale, or
 *  - the entry for the active locale is an empty string.
 *
 * Components register `bind*` listeners; calling {@link Localisation.setLocale}
 * triggers all bindings to re-evaluate and update their target DOM nodes
 * in place, so users see the new language without re-creating controls.
 */
export class Localisation {
    private locale = '';
    private updaters = new Set<Updater>();

    /**
     * Set the active locale and notify every registered binding.
     * Pass an empty string to reset to fallback strings.
     */
    public setLocale(locale: string): void {
        this.locale = locale ?? '';
        this.updaters.forEach(u => u());
    }

    /** @returns The active locale, or empty string if none is set. */
    public getLocale(): string {
        return this.locale;
    }

    /**
     * Resolve a localisation map against the current locale,
     * falling back to the supplied default string.
     */
    public resolve(map: LocalisationMap | undefined, fallback = ''): string {
        if (
            map &&
            this.locale &&
            typeof map[this.locale] === 'string' &&
            map[this.locale] !== ''
        ) {
            return map[this.locale];
        }
        return fallback ?? '';
    }

    /**
     * Generic binding: register a setter that receives the resolved string
     * now and on every locale change. Returns an unsubscribe function.
     */
    public bind(
        apply: (text: string) => void,
        map: LocalisationMap | undefined,
        fallback = ''
    ): () => void {
        const update: Updater = () => apply(this.resolve(map, fallback));
        update();
        this.updaters.add(update);
        return () => {
            this.updaters.delete(update);
        };
    }

    /**
     * Like {@link bind} but accepts a supplier function so the map is
     * re-read on every locale change (lazy evaluation).
     */
    public bindLazy(
        apply: (text: string) => void,
        mapFn: () => LocalisationMap | undefined,
        fallback = ''
    ): () => void {
        const update: Updater = () => apply(this.resolve(mapFn(), fallback));
        update();
        this.updaters.add(update);
        return () => {
            this.updaters.delete(update);
        };
    }

    /** Bind to `element.innerText`. */
    public bindText(
        element: HTMLElement,
        map: LocalisationMap | undefined,
        fallback = ''
    ): () => void {
        return this.bind(t => { element.innerText = t; }, map, fallback);
    }

    /** Bind to `element.innerHTML` (use only with trusted content). */
    public bindHTML(
        element: HTMLElement,
        map: LocalisationMap | undefined,
        fallback = ''
    ): () => void {
        return this.bind(t => { element.innerHTML = t; }, map, fallback);
    }

    /** Bind to `element.title` (HTML tooltip attribute). */
    public bindTitle(
        element: HTMLElement,
        map: LocalisationMap | undefined,
        fallback = ''
    ): () => void {
        return this.bind(t => { element.title = t; }, map, fallback);
    }

    /**
     * Remove all bindings. Called on map destroy / control clear to avoid
     * dangling references to detached DOM elements.
     */
    public clear(): void {
        this.updaters.clear();
    }
}

export default Localisation;

